// FalModelDetail — full view of a single fal.ai catalog entry (PX-94).
//
// The endpoint_id from the URL carries slashes (e.g.
// `fal-ai/nano-banana-2/edit`), so the route uses a splat (`*`) and we
// read the rest via `useParams()['*']`.

import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { APIError, request } from '@/lib/api'
import type { FalModelDetail } from '@/types/api'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-500">{title}</h2>
      {children}
    </section>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="text-sm text-ink-900">{value || <span className="text-ink-400">—</span>}</dd>
    </div>
  )
}

function JSONViewer({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return <p className="text-sm text-ink-400">No schema available — sync may not have populated this row.</p>
  return (
    <pre className="max-h-[480px] overflow-auto rounded-lg bg-ink-900 p-4 text-xs leading-relaxed text-ink-100">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

export function FalModelDetailPage() {
  // Splat path — react-router gives the matched portion via params['*'].
  const params = useParams()
  const endpointId = params['*'] ?? ''

  const detailQuery = useQuery({
    queryKey: ['admin', 'fal-models', 'detail', endpointId],
    queryFn: () => request<FalModelDetail>(`/admin/fal-models/${endpointId}`),
    enabled: !!endpointId,
  })

  if (detailQuery.isLoading) {
    return <div className="p-6 text-sm text-ink-400">Loading…</div>
  }

  if (detailQuery.error instanceof APIError && detailQuery.error.status === 404) {
    return (
      <div className="p-6">
        <Link to="/fal-models" className="text-sm text-brand-600 hover:underline">← Back to catalog</Link>
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          Model <code className="font-mono">{endpointId}</code> not found.
        </div>
      </div>
    )
  }

  if (!detailQuery.data) return null
  const m = detailQuery.data

  const pricingFormatted = m.pricing_unit && m.pricing_amount_usd
    ? `$${Number(m.pricing_amount_usd).toString()} ${m.pricing_unit.replace('per_', 'per ').replace('_', ' ')}`
    : null

  return (
    <div className="space-y-5 p-6">
      <Link to="/fal-models" className="text-sm text-brand-600 hover:underline">← Back to catalog</Link>

      <header className="flex items-start gap-5 rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
        {m.thumbnail_url ? (
          <img src={m.thumbnail_url} alt="" className="h-32 w-32 rounded-lg object-cover" />
        ) : (
          <div className="h-32 w-32 rounded-lg bg-ink-100" />
        )}
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{m.title}</h1>
            {m.deprecated && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs uppercase tracking-wide text-red-700">
                deprecated
              </span>
            )}
          </div>
          <code className="text-xs text-ink-500">{m.endpoint_id}</code>
          {m.short_description && (
            <p className="text-sm text-ink-700">{m.short_description}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-2">
            {m.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700">
                {tag}
              </span>
            ))}
          </div>
          {m.model_url && (
            <a
              href={m.model_url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex w-fit items-center gap-1 text-sm text-brand-600 hover:underline"
            >
              Open on fal.ai ↗
            </a>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Section title="Metadata">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Category" value={m.category} />
            <Field label="Lab" value={m.model_lab} />
            <Field label="Family" value={m.model_family} />
            <Field label="Kind" value={m.kind} />
            <Field label="Hosting" value={m.hosting_type} />
            <Field label="License" value={m.license_type} />
            <Field label="Status" value={m.status} />
            <Field
              label="Published"
              value={m.published_at ? new Date(m.published_at).toLocaleDateString() : null}
            />
            <Field
              label="Last synced"
              value={new Date(m.last_synced_at).toLocaleString()}
            />
            <Field
              label="Duration estimate"
              value={m.duration_estimate ? `${m.duration_estimate}s` : null}
            />
          </dl>
        </Section>

        <Section title="Pricing">
          {pricingFormatted ? (
            <div className="mb-3 text-xl font-mono text-brand-600">{pricingFormatted}</div>
          ) : (
            <div className="mb-3 text-sm text-ink-500">
              Headline parser bailed — see raw markdown below.
            </div>
          )}
          {m.pricing_raw_md && (
            <div className="rounded-lg bg-ink-50 p-3 text-sm text-ink-700">
              <div className="mb-1 text-xs uppercase tracking-wide text-ink-400">Raw from fal.ai</div>
              <p className="whitespace-pre-wrap leading-relaxed">{m.pricing_raw_md}</p>
            </div>
          )}
          {!m.pricing_raw_md && (
            <p className="text-sm text-ink-400">No pricing information available.</p>
          )}
        </Section>
      </div>

      <Section title={`Input schema${m.input_properties.length ? ` — ${m.input_properties.length} properties` : ''}`}>
        <JSONViewer data={m.input_schema} />
      </Section>

      <Section title="Output schema">
        <JSONViewer data={m.output_schema} />
      </Section>
    </div>
  )
}
